import { Lightbulb, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface Tip {
  id: string;
  title: string;
  description: string;
  link: string;
  icon: string;
  position: number;
}

export function HelpSectionCompact() {
  const [currentTip, setCurrentTip] = useState(0);

  const { data: tips = [], isLoading } = useQuery({
    queryKey: ['tips'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tips')
        .select('*')
        .eq('is_active', true)
        .order('position', { ascending: true });
      
      if (error) throw error;
      return data as Tip[];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const nextTip = () => {
    if (tips.length > 0) {
      setCurrentTip((prev) => (prev + 1) % tips.length);
    }
  };

  const prevTip = () => {
    if (tips.length > 0) {
      setCurrentTip((prev) => (prev - 1 + tips.length) % tips.length);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-4 w-24 bg-muted rounded" />
        <div className="h-[140px] bg-muted rounded" />
      </div>
    );
  }

  if (tips.length === 0) {
    return null;
  }

  const tip = tips[currentTip];

  return (
    <div>
      <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
        <Lightbulb className="h-3.5 w-3.5" />
        Aide et astuces
      </div>
      
      <div className="bg-card rounded-lg p-4 border border-border min-h-[140px] flex flex-col">
        <h4 className="text-sm font-medium text-foreground leading-tight mb-2">{tip.title}</h4>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed flex-1">{tip.description}</p>
        
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" className="h-7 text-xs" asChild>
            <Link to={tip.link} className="flex items-center gap-1">
              Découvrir
              <ArrowRight className="h-3 w-3" />
            </Link>
          </Button>
          
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {tips.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTip(index)}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    index === currentTip ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                />
              ))}
            </div>
            <div className="flex">
              <Button variant="ghost" size="icon" onClick={prevTip} className="h-6 w-6">
                <ChevronLeft className="h-3 w-3" />
              </Button>
              <Button variant="ghost" size="icon" onClick={nextTip} className="h-6 w-6">
                <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
